import Axios from "../../../utils/axiosInterceptor";
import { PATIENT_APIS } from "../../../utils/constants";
import {
  buildFhplCasePreAuthOthersRequest,
  type FhplPreAuthFormValues,
} from "./fhplCasePreAuthOthersMapper";

export type { FhplPreAuthFormValues } from "./fhplCasePreAuthOthersMapper";
export {
  buildFhplCasePreAuthOthersRequest,
  mapFhplPreAuthOthersToForm,
  FHPL_DURATION_UNITS,
} from "./fhplCasePreAuthOthersMapper";

export const FHPL_PAYER_NAME = "FHPL";
export const FHPL_DATE_FORMAT = "DD/MM/YYYY";
export const FHPL_DATETIME_FORMAT = "DD/MM/YYYY HH:mm:ss";

const FHPL_API_BASE = PATIENT_APIS.FHPL_SERVICE;

export const FHPL_GENDER_OPTIONS = ["Male", "Female", "Other"] as const;

export const FHPL_RELATIONSHIP_OPTIONS = [
  "Self",
  "Spouse",
  "Son",
  "Daughter",
  "Father",
  "Mother",
  "Brother",
  "Sister",
] as const;

export const FHPL_CLASS_OF_ACCOMMODATION_OPTIONS = [
  "General Ward",
  "Semi Private",
  "Private",
  "Deluxe",
  "Suite",
  "ICU",
  "Day Care",
] as const;

export const FHPL_PROPOSED_LINE_OF_TREATMENT_OPTIONS = [
  "Intensive Care",
  "Investigation",
  "Medical Management",
  "Non Allopathic Treatment",
  "Surgical Management",
] as const;

export const FHPL_ROUTE_OF_ADMINISTRATION_OPTIONS = [
  "IV",
  "IM",
  "IV % IM",
  "Parental",
  "Oral",
] as const;

export const FHPL_ADMISSION_TYPE_OPTIONS = [
  "Planned",
  "Emergency",
  "Day Care",
  "Maternity",
  "OPD",
] as const;

export const FHPL_ANY_OTHER_DISEASE_OPTIONS = [
  "Select",
  "Yes",
  "No",
] as const;

export type FhplPatientFetchDetails = {
  hospitalId: number;
  schemeId: number;
  portalId: number;
  uhid: string;
  firstName: string;
};

export type BuildFhplPreAuthPayloadParams = {
  schemeMetaDataId: number | string | undefined;
  portalMetaDataId?: number;
  casePreAuthId?: number;
  hospitalId?: number;
  schemeId?: number;
  beneficiaryId?: string;
  patientOpdIpdNumber?: string | number | undefined;
  integratedWithPortal?: boolean;
  action?: string;
  formValues: FhplPreAuthFormValues;
};

export type FhplApprovePayload = {
  caseId: number;
  visitId: number;
  stage: string;
  approved: boolean;
  casePreAuthId: number;
  portalMetaDataId?: number;
  remarks?: string;
  preAuthRequest: ReturnType<typeof buildFhplPreAuthPayload>;
};

export const getFhplPrefillFromBasicDetails = (basicDetails: any) => {
  const patient = basicDetails?.patientResponse;
  const cases = basicDetails?.casesResponse;
  const visit = basicDetails?.visitInfoResponse;
  const scheme =
    patient?.patientSchemeNewResponseList?.[0] ||
    basicDetails?.patientSchemeNewResponseList?.[0];

  return {
    patientName:
      patient?.fullName || patient?.name || cases?.patientName || "",
    gender: patient?.gender || patient?.sex || undefined,
    relationship: scheme?.relationship || "Self",
    dateOfBirth: patient?.dob || null,
    age: patient?.age ?? null,
    ageUnit: "Year" as const,
    patientMobile: patient?.primaryMobile || "",
    policyNumber:
      patient?.patientSchemeNewResponseList?.[0]?.policyNumber ||
      patient?.policyNumber ||
      scheme?.policyNumber ||
      "",
    treatingDoctorName: cases?.doctorName || "",
    probableAdmissionDate: visit?.doa || cases?.doa || null,
  };
};

export const mapFhplPortalData = (response: any) => {
  if (!response || typeof response !== "object") {
    return null;
  }
  return {
    casePreAuthOthersResponse: response,
    casePreAuthFhpl: response,
  };
};

export const buildFhplPreAuthPayload = ({
  schemeMetaDataId,
  portalMetaDataId,
  casePreAuthId = 0,
  hospitalId,
  schemeId,
  beneficiaryId,
  patientOpdIpdNumber,
  integratedWithPortal = true,
  action = "save",
  formValues,
}: BuildFhplPreAuthPayloadParams) => {
  const casePreAuthOthersRequest = buildFhplCasePreAuthOthersRequest(formValues);

  return {
    schemeMetaDataId: String(schemeMetaDataId),
    portalMetaDataId,
    casePreAuthId,
    casePreAuthRequest: {
      beneficiaryId,
      patientOpdIpdNumber,
    },
    casePreAuthOthersRequest,
    casePreAuthIhxRequest: null,
    casePreAuthCghsRequest: null,
    casePreAuthVidalRequest: null,
    casePreAuthStarhealthRequest: null,
    casePreAuthEchsRequest: null,
    hospitalId,
    schemeId,
    totalAmount:
      formValues?.totalEstimatedCost != null
        ? String(formValues.totalEstimatedCost)
        : "",
    integratedWithPortal,
    action,
    doa: formValues?.probableAdmissionDate || null,
    dod: null,
  };
};

export const buildFhplApprovePayload = (
  params: Omit<FhplApprovePayload, "preAuthRequest"> & {
    preAuthRequest: ReturnType<typeof buildFhplPreAuthPayload>;
  }
): FhplApprovePayload => ({
  caseId: params.caseId,
  visitId: params.visitId,
  stage: params.stage,
  approved: params.approved,
  casePreAuthId: params.casePreAuthId,
  portalMetaDataId: params.portalMetaDataId,
  remarks: params.remarks,
  preAuthRequest: params.preAuthRequest,
});

export const fetchFhplBeneficiaryVerification = async (
  data: FhplPatientFetchDetails
) => {
  const hospitalId = data?.hospitalId;
  const schemeId = data?.schemeId;
  const portalId = data?.portalId;
  const uhid = data?.uhid?.trim() ?? "";
  const firstName = data?.firstName?.trim() ?? "";

  if (!hospitalId || !schemeId || !portalId) {
    throw new Error("Hospital, scheme and portal details are required");
  }

  if (!uhid) {
    throw new Error("UHID is required to fetch FHPL beneficiary details");
  }

  try {
    const response = await Axios.post(
      `${FHPL_API_BASE}/fetchBeneficiaryVerification`,
      {
        hospital_id: hospitalId,
        scheme_id: schemeId,
        portal_id: portalId,
        uhid,
        first_name: firstName,
      }
    );
    return response?.data ?? null;
  } catch (error: any) {
    console.error(
      "Failed to fetch FHPL beneficiary details:",
      error?.response?.data
    );
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to fetch FHPL beneficiary details"
    );
  }
};

export const getAllCasePreAuthByCaseIdAndVisitId = async (
  caseId: number,
  visitId: number
) => {
  try {
    const response = await Axios.get(
      `${FHPL_API_BASE}/cases/${caseId}/visitInfo/${visitId}/getAllCasePreAuthByCaseIdAndVisitId`
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to get FHPL pre-auth:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to get FHPL pre-auth data"
    );
  }
};

export const fetchPortalDataFhpl = getAllCasePreAuthByCaseIdAndVisitId;

export const saveDraftFhpl = async (
  caseId: number,
  visitId: number,
  formData: any
) => {
  try {
    const response = await Axios.post(
      `${FHPL_API_BASE}/cases/${caseId}/visitInfo/${visitId}/casePreAuth`,
      formData
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot save FHPL pre-auth:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Cannot save FHPL pre-auth details"
    );
  }
};

export const updateDraftFhpl = async (
  caseId: number,
  visitId: number,
  casePreAuthId: number,
  formData: any
) => {
  try {
    const response = await Axios.patch(
      `${FHPL_API_BASE}/cases/${caseId}/visitInfo/${visitId}/casePreAuthId/${casePreAuthId}`,
      formData
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot update FHPL pre-auth:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Cannot update FHPL pre-auth details"
    );
  }
};

export const intimateFhplPreAuth = async (
  caseId: number,
  visitId: number,
  formData: any
) =>
  saveDraftFhpl(caseId, visitId, {
    ...formData,
    action: "intimate",
  });

export const initiateCasePreAuthFhpl = async (
  caseId: number,
  visitId: number,
  payload: any
) => {
  try {
    const response = await Axios.post(
      `${FHPL_API_BASE}/cases/${caseId}/visitInfo/${visitId}/initiateCasePreAuth`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to initiate FHPL pre-auth on portal:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        "Failed to initiate FHPL pre-auth on portal"
    );
  }
};

export const approveFhplPreAuth = async (formData: FhplApprovePayload) => {
  try {
    const response = await Axios.post(`${FHPL_API_BASE}/approve`, formData);
    return response.data;
  } catch (error: any) {
    console.error("Failed to approve FHPL pre-auth:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to approve FHPL pre-auth"
    );
  }
};

export const claimValidationFhpl = async (data: any) => {
  try {
    const response = await Axios.post(`${FHPL_API_BASE}/claim/validate`, data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to validate FHPL claim:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to validate FHPL claim"
    );
  }
};

export const updateClaimValidationFhpl = async (
  caseId: number,
  visitId: number,
  data: any
) => {
  try {
    const response = await Axios.patch(
      `${FHPL_API_BASE}/claim/caseId/${caseId}/visitId/${visitId}/update`,
      data
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to update FHPL claim validation:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to update FHPL claim validation"
    );
  }
};
